import { base64Decode } from "./encoding";
import { controlNullifier, reserveNullifier } from "./field";
import type { V2KeyContext, V2Keys } from "./keys";
import { createMerkleAccumulator } from "./merkle";
import { decryptNote, type V2Note } from "./notes";

export type ChainNoteRecord = {
  index: number;
  commitment: string;
  kind: "reserve" | "control";
  encrypted_note: string;
  block_height: number;
};

export type ChainNullifierRecord = {
  index: number;
  nullifier: string;
};

export type RecoveredNote = {
  index: number;
  blockHeight: number;
  note: V2Note;
  nullifier: string;
  spent: boolean;
  pathElements: string[];
  pathIndices: string[];
  /** Length in bytes of the note's encrypted payload on chain (public). */
  encryptedLength?: number;
};

/**
 * Public statistics of the whole pool, counted while scanning it (no extra
 * requests). Local only: the privacy indicator compares the user's notes with
 * them, nothing is sent anywhere.
 */
export type RecoveryPoolStats = {
  /** Notes in the pinned tree (its next_index). */
  nextIndex: number;
  /** Spent notes in the pinned tree (its next_nullifier_index). */
  nextNullifierIndex: number;
  /** Records per kind in the pinned tree. */
  records: { reserve: number; control: number };
  /** Encrypted payload length in bytes → reserve records of that length. */
  reserveLengths: ReadonlyMap<number, number>;
};

export type RecoverySnapshot = {
  nextIndex: number;
  nextNullifierIndex: number;
  root: string;
};

export type RecoverySource = {
  fetchNotes: (
    startAfter: number | undefined,
    limit: number
  ) => Promise<{ notes: ChainNoteRecord[] }>;
  fetchNullifiers: (
    startAfter: number | undefined,
    limit: number
  ) => Promise<{ nullifiers: ChainNullifierRecord[] }>;
  snapshot: () => Promise<RecoverySnapshot>;
  /** Optional direct-contract source used after a stale/corrupt cache mismatch. */
  authoritativeFallback?: RecoverySource;
};

const mapConcurrent = async <T, R>(
  values: readonly T[],
  concurrency: number,
  operation: (value: T) => Promise<R>
): Promise<R[]> => {
  const result = new Array<R>(values.length);
  let cursor = 0;
  const worker = async () => {
    for (;;) {
      const index = cursor;
      cursor += 1;
      if (index >= values.length) return;
      result[index] = await operation(values[index]);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, values.length) }, worker)
  );
  return result;
};

export const recoverFromChain = async (params: {
  keys: V2Keys;
  context: V2KeyContext;
  source: RecoverySource;
}): Promise<{
  root: string;
  notes: RecoveredNote[];
  activeNotes: RecoveredNote[];
  pool?: RecoveryPoolStats;
}> => {
  const recover = async (
    source: RecoverySource
  ): Promise<{
    root: string;
    notes: RecoveredNote[];
    activeNotes: RecoveredNote[];
    pool: RecoveryPoolStats;
  }> => {
    // Pin the tree before paging. New notes may land while recovery runs, but
    // they cannot make this internally consistent historical snapshot flaky.
    const snapshot = await source.snapshot();
    const tree = await createMerkleAccumulator();
    const owned: Array<{
      record: ChainNoteRecord;
      note: V2Note;
      encryptedLength: number;
    }> = [];
    const records = { reserve: 0, control: 0 };
    const reserveLengths = new Map<number, number>();
    const limit = 200;
    let lastIndex: number | undefined;
    while (tree.length < snapshot.nextIndex) {
      const remaining = snapshot.nextIndex - tree.length;
      const requested = Math.min(limit, remaining);
      const page = await source.fetchNotes(lastIndex, requested);
      if (page.notes.length === 0)
        throw new Error("On-chain note history ended before the pinned tree");
      if (page.notes.length > requested)
        throw new Error("On-chain note page exceeds the requested limit");
      const decrypted = await mapConcurrent(page.notes, 8, async (record) => {
        const encrypted = base64Decode(record.encrypted_note);
        return {
          record,
          encryptedLength: encrypted.length,
          note: await decryptNote({
            keys: params.keys,
            context: params.context,
            kind: record.kind,
            commitment: record.commitment,
            encrypted,
          }),
        };
      });
      for (const { record, note, encryptedLength } of decrypted) {
        if (record.index !== tree.length)
          throw new Error("On-chain note history contains a gap or duplicate");
        await tree.append(record.commitment, note !== null);
        // Public pool statistics for the privacy indicator (every record).
        records[record.kind] += 1;
        if (record.kind === "reserve")
          reserveLengths.set(
            encryptedLength,
            (reserveLengths.get(encryptedLength) ?? 0) + 1
          );
        if (note) owned.push({ record, note, encryptedLength });
        lastIndex = record.index;
      }
    }
    const recoveredRoot = await tree.finalize();
    if (recoveredRoot !== snapshot.root)
      throw new Error("Recovered Merkle root does not match the contract");
    const spent = new Set<string>();
    let nullifierCount = 0;
    let lastNullifierIndex: number | undefined;
    while (nullifierCount < snapshot.nextNullifierIndex) {
      const requested = Math.min(
        limit,
        snapshot.nextNullifierIndex - nullifierCount
      );
      const page = await source.fetchNullifiers(lastNullifierIndex, requested);
      if (page.nullifiers.length === 0) {
        throw new Error(
          "On-chain nullifier history ended before the pinned snapshot"
        );
      }
      if (page.nullifiers.length > requested) {
        throw new Error("On-chain nullifier page exceeds the requested limit");
      }
      for (const record of page.nullifiers) {
        if (record.index !== nullifierCount) {
          throw new Error("On-chain nullifier history contains a gap");
        }
        spent.add(record.nullifier);
        lastNullifierIndex = record.index;
        nullifierCount += 1;
      }
    }
    const notes = await mapConcurrent(
      owned,
      8,
      async ({ record, note, encryptedLength }) => {
        const nullifier =
          note.kind === "reserve"
            ? await reserveNullifier(note.ownerKey, note.rho)
            : await controlNullifier(note.ownerKey, note.rho);
        return {
          index: record.index,
          blockHeight: record.block_height,
          note,
          nullifier,
          spent: spent.has(nullifier),
          encryptedLength,
          ...tree.path(record.index),
        };
      }
    );
    return {
      root: recoveredRoot,
      notes,
      activeNotes: notes.filter((note) => !note.spent),
      pool: {
        nextIndex: snapshot.nextIndex,
        nextNullifierIndex: snapshot.nextNullifierIndex,
        records,
        reserveLengths,
      },
    };
  };

  try {
    return await recover(params.source);
  } catch (error) {
    if (!params.source.authoritativeFallback) throw error;
    return recover(params.source.authoritativeFallback);
  }
};

/** Beyond this many new records or nullifiers the check gives up (changed). */
const OWN_CHANGE_CHECK_LIMIT = 1_000;

/**
 * Whether the user's own Reserve notes changed between a scan and a later tree
 * read. Reads only the records and nullifiers added since the scan (the same
 * range reads any scan makes, identical for every user) and checks them
 * locally: a new record that decrypts as the user's Reserve note, or a new
 * nullifier of one of their unspent Reserve notes. Anything unexpected (a
 * lagging node, a gap, too much to read) counts as changed.
 */
export const ownReserveChangedSince = async (params: {
  keys: V2Keys;
  context: V2KeyContext;
  source: RecoverySource;
  scanned: { nextIndex: number; nextNullifierIndex: number };
  now: { nextIndex: number; nextNullifierIndex: number };
  /** Nullifiers of the user's unspent Reserve notes in the scan. */
  unspentNullifiers: ReadonlySet<string>;
}): Promise<boolean> => {
  const { scanned, now, source } = params;
  if (
    now.nextIndex < scanned.nextIndex ||
    now.nextNullifierIndex < scanned.nextNullifierIndex ||
    now.nextIndex - scanned.nextIndex > OWN_CHANGE_CHECK_LIMIT ||
    now.nextNullifierIndex - scanned.nextNullifierIndex > OWN_CHANGE_CHECK_LIMIT
  )
    return true;
  const limit = 200;
  let index = scanned.nextIndex;
  while (index < now.nextIndex) {
    const requested = Math.min(limit, now.nextIndex - index);
    const page = await source.fetchNotes(
      index > 0 ? index - 1 : undefined,
      requested
    );
    if (page.notes.length === 0 || page.notes.length > requested) return true;
    for (const record of page.notes) {
      if (record.index !== index) return true;
      if (
        record.kind === "reserve" &&
        (await decryptNote({
          keys: params.keys,
          context: params.context,
          kind: record.kind,
          commitment: record.commitment,
          encrypted: base64Decode(record.encrypted_note),
        })) !== null
      )
        return true;
      index += 1;
    }
  }
  let nullifierIndex = scanned.nextNullifierIndex;
  while (nullifierIndex < now.nextNullifierIndex) {
    const requested = Math.min(limit, now.nextNullifierIndex - nullifierIndex);
    const page = await source.fetchNullifiers(
      nullifierIndex > 0 ? nullifierIndex - 1 : undefined,
      requested
    );
    if (page.nullifiers.length === 0 || page.nullifiers.length > requested)
      return true;
    for (const record of page.nullifiers) {
      if (record.index !== nullifierIndex) return true;
      if (params.unspentNullifiers.has(record.nullifier)) return true;
      nullifierIndex += 1;
    }
  }
  return false;
};
